import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  ReactNode,
  useEffect,
  lazy,
  Suspense
} from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import type { ToastAction, ToastType } from './Toast';
import { registerToastHandlers, unregisterToastHandlers } from './toastUtils';

// Lazy: ToastContainer (→ Toast.tsx) importa framer-motion (~455KB). Este provider
// es eager (global.tsx), así que tenerlo estático arrastraba framer-motion al bundle
// eager. Lazy + <ErrorBoundary fallback={null}>: si el chunk falla al cargar NO tira
// toda la app (un <Suspense> pelado ARRIBA del ErrorBoundary de la app = white-screen,
// lección del code review). Los toasts aparecen al cargar el chunk (post primer paint);
// un toast en los primeros ms es raro y aceptable.
const ToastContainer = lazy(() => import('./ToastContainer'));

interface ToastOptions {
  message: string;
  description?: string;
  duration?: number;
  type?: ToastType;
  action?: ToastAction;
}

interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  description?: string;
  duration?: number;
  action?: ToastAction;
}

interface ToastContextValue {
  success: (message: string, description?: string, duration?: number, action?: ToastAction) => string;
  error: (message: string, description?: string, duration?: number, action?: ToastAction) => string;
  warning: (message: string, description?: string, duration?: number, action?: ToastAction) => string;
  info: (message: string, description?: string, duration?: number, action?: ToastAction) => string;
  loading: (message: string, description?: string) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
  update: (id: string, options: Partial<ToastOptions>) => void;
  promise: <T>(
    promise: Promise<T>,
    options: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((err: unknown) => string);
    }
  ) => Promise<T>;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let toastIdCounter = 0;
const generateId = () => `toast-${++toastIdCounter}-${Date.now()}`;

interface ToastProviderProps {
  children: ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'bottom-left',
  maxToasts = 5
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // We need to store addToast in a ref to avoid circular dependency issues
  const addToastRef = React.useRef<
    (
      type: ToastType,
      message: string,
      description?: string,
      duration?: number,
      action?: ToastAction
    ) => string
  >(() => '');

  const addToast = useCallback(
    (
      type: ToastType,
      message: string,
      description?: string,
      duration?: number,
      action?: ToastAction
    ): string => {
      const id = generateId();
      const newToast: ToastItem = {
        id,
        type,
        message,
        description,
        action,
        duration: duration ?? (type === 'loading' ? undefined : 5000)
      };

      setToasts((prev) => {
        const updated = [...prev, newToast];
        // Limit the number of visible toasts
        if (updated.length > maxToasts) {
          return updated.slice(-maxToasts);
        }
        return updated;
      });

      return id;
    },
    [maxToasts]
  );

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const update = useCallback((id: string, options: Partial<ToastOptions>) => {
    setToasts((prev) =>
      prev.map((toastItem) =>
        toastItem.id === id
          ? {
              ...toastItem,
              ...options
            }
          : toastItem
      )
    );
  }, []);

  // Register global toast handlers for imperative API usage
  useEffect(() => {
    // Update the ref inside the effect to avoid lint errors
    addToastRef.current = addToast;
    const globalUpdate = (
      id: string,
      options: { message?: string; description?: string; type?: ToastType }
    ) => {
      update(id, options);
    };

    registerToastHandlers(
      (type, message, description, duration, action) =>
        addToastRef.current(type, message, description, duration, action),
      dismiss,
      dismissAll,
      globalUpdate
    );

    return () => {
      unregisterToastHandlers();
    };
  }, [addToast, dismiss, dismissAll, update]);

  const success = useCallback(
    (message: string, description?: string, duration?: number, action?: ToastAction) =>
      addToast('success', message, description, duration, action),
    [addToast]
  );

  const error = useCallback(
    (message: string, description?: string, duration?: number, action?: ToastAction) =>
      addToast('error', message, description, duration, action),
    [addToast]
  );

  const warning = useCallback(
    (message: string, description?: string, duration?: number, action?: ToastAction) =>
      addToast('warning', message, description, duration, action),
    [addToast]
  );

  const info = useCallback(
    (message: string, description?: string, duration?: number, action?: ToastAction) =>
      addToast('info', message, description, duration, action),
    [addToast]
  );

  const loading = useCallback(
    (message: string, description?: string) => addToast('loading', message, description),
    [addToast]
  );

  const toastPromise = useCallback(
    async <T,>(
      promise: Promise<T>,
      options: {
        loading: string;
        success: string | ((data: T) => string);
        error: string | ((err: unknown) => string);
      }
    ): Promise<T> => {
      const id = loading(options.loading);

      try {
        const result = await promise;
        dismiss(id);
        const successMessage =
          typeof options.success === 'function' ? options.success(result) : options.success;
        success(successMessage);
        return result;
      } catch (err) {
        dismiss(id);
        const errorMessage = typeof options.error === 'function' ? options.error(err) : options.error;
        error(errorMessage);
        throw err;
      }
    },
    [loading, dismiss, success, error]
  );

  const value: ToastContextValue = {
    success,
    error,
    warning,
    info,
    loading,
    dismiss,
    dismissAll,
    update,
    promise: toastPromise
  };

  return (
    <ToastContext value={value}>
      {children}
      <ErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          <ToastContainer toasts={toasts} onClose={dismiss} position={position} />
        </Suspense>
      </ErrorBoundary>
    </ToastContext>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
